/**
 * PendingInviteCard Component
 *
 * Displays a pending team invite with expiry info and revoke action.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Mail, Clock, X, Send } from 'lucide-react-native';
import Animated, { FadeIn, Layout } from 'react-native-reanimated';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import type { TeamInvite, TeamRole } from '@/lib/team';
import { ROLE_NAMES } from '@/lib/team';

// ============================================================================
// Types
// ============================================================================

interface PendingInviteCardProps {
  invite: TeamInvite;
  onRevoke?: (inviteId: string) => void;
  onResend?: (inviteId: string) => void;
  index?: number;
}

// ============================================================================
// Component
// ============================================================================

export function PendingInviteCard({
  invite,
  onRevoke,
  onResend,
  index = 0,
}: PendingInviteCardProps) {
  const colors = useThemeColors();

  // Calculate days until expiry
  const expiresAt = new Date(invite.expiresAt);
  const now = new Date();
  const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isExpiringSoon = daysLeft <= 2;

  return (
    <Animated.View
      entering={FadeIn.delay(index * 50).duration(300)}
      layout={Layout.springify()}
    >
      <View
        style={[
          styles.container,
          {
            backgroundColor: colors.surface.primary,
            borderColor: colors.border.light,
          },
        ]}
      >
        {/* Icon */}
        <View
          style={[
            styles.icon,
            { backgroundColor: colors.primary[50] },
          ]}
        >
          <Mail size={20} color={colors.primary[500]} />
        </View>

        {/* Info */}
        <View style={styles.info}>
          <Text
            style={[styles.email, { color: colors.text.primary }]}
            numberOfLines={1}
          >
            {invite.email}
          </Text>
          <View style={styles.metaRow}>
            <View
              style={[
                styles.rolePill,
                { backgroundColor: colors.surface.secondary },
              ]}
            >
              <Text style={[styles.roleText, { color: colors.text.secondary }]}>
                {ROLE_NAMES[invite.role]}
              </Text>
            </View>
            <View style={styles.expiryRow}>
              <Clock
                size={12}
                color={isExpiringSoon ? colors.warning : colors.text.tertiary}
              />
              <Text
                style={[
                  styles.expiryText,
                  { color: isExpiringSoon ? colors.warning : colors.text.tertiary },
                ]}
              >
                {daysLeft <= 0 ? 'Expired' : `${daysLeft}d left`}
              </Text>
            </View>
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          {onResend && (
            <AnimatedPressable
              onPress={() => onResend(invite.id)}
              style={[
                styles.actionButton,
                {
                  backgroundColor: colors.primary[50],
                },
              ]}
            >
              <Send size={16} color={colors.primary[600]} />
            </AnimatedPressable>
          )}
          {onRevoke && (
            <AnimatedPressable
              onPress={() => onRevoke(invite.id)}
              style={[
                styles.actionButton,
                {
                  backgroundColor: colors.surface.secondary,
                },
              ]}
            >
              <X size={16} color={colors.error} />
            </AnimatedPressable>
          )}
        </View>
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    gap: 12,
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flex: 1,
    gap: 6,
  },
  email: {
    fontSize: 14,
    fontWeight: '500',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  rolePill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  roleText: {
    fontSize: 11,
    fontWeight: '500',
  },
  expiryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  expiryText: {
    fontSize: 11,
    fontWeight: '500',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
